import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { assetService } from '../../services/assetService';
import { buildAssetPreviewUrl } from '../../utils/mediaUrl';
import type { EmbeddedImageData } from './embeddedImageUtils';
import {
  buildEmbeddedImageSrc,
  buildOssEmbeddedCropProcess,
  withOssProcess,
} from './embeddedImageUtils';
import { TtqibZoomableImageOverlay } from './TtqibZoomableImageOverlay';

type TtqibEmbeddedImageProps = {
  record?: EmbeddedImageData;
  fullSourceUrl?: string;
  /** 私有 OSS：签名时带上 crop process，避免拉整图 */
  ossId?: string | null;
  height: number;
  width?: number;
  zoomable?: boolean;
  zoomTitle?: string;
};

/** Align with Mobile2: OSS crop first; client full-image clip only as last resort. */
const EMBEDDED_CROP_CLIENT_FALLBACK_ENABLED = false;

type LoadMode = 'oss-process' | 'client-full';

function toHttps(url: string): string {
  return url.replace(/^http:\/\//i, 'https://');
}

/** 私有桶直链无签名时无法带 process；从 URL path 还原 oss key 以便签名裁切。 */
function extractOssKeyFromUrl(rawUrl?: string | null): string {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
    const key = parsed.pathname.replace(/^\/+/, '').trim();
    if (key.startsWith('assets/') || key.startsWith('ttq/')) return key;
  } catch {
    // ignore
  }
  return '';
}

function resolveTargetPixelWidth(displayWidth: number, displayHeight: number): number {
  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
  return Math.max(64, Math.ceil(Math.max(displayWidth, displayHeight) * dpr));
}

export function TtqibEmbeddedImage({
  record,
  fullSourceUrl,
  ossId,
  height,
  width = 260,
  zoomable = false,
  zoomTitle,
}: TtqibEmbeddedImageProps) {
  const rawUrl = (record?.sourceImageUrl ?? fullSourceUrl ?? '').trim();
  const resolvedOssId = String(ossId || '').trim() || extractOssKeyFromUrl(rawUrl);
  const resolvedZoomTitle = zoomTitle || (record ? '嵌入图' : '原始素材');
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [resolving, setResolving] = useState(Boolean(rawUrl || resolvedOssId));
  const [viewerOpen, setViewerOpen] = useState(false);
  const [loadMode, setLoadMode] = useState<LoadMode>('oss-process');
  const cropSrcRef = useRef<string | null>(null);
  const attemptRef = useRef(0);
  const loadModeRef = useRef<LoadMode>('oss-process');
  loadModeRef.current = loadMode;

  const targetPixelWidth = useMemo(() => {
    if (!record) return undefined;
    const scale = Math.min(width / record.cropRect.width, height / record.cropRect.height);
    const displayWidth = Math.round(record.cropRect.width * scale);
    const displayHeight = Math.round(record.cropRect.height * scale);
    return resolveTargetPixelWidth(displayWidth, displayHeight);
  }, [height, record, width]);

  const ossCropProcess = useMemo(() => {
    if (!record) return undefined;
    return buildOssEmbeddedCropProcess(record, targetPixelWidth);
  }, [record, targetPixelWidth]);

  useEffect(() => {
    let cancelled = false;
    const attempt = ++attemptRef.current;
    cropSrcRef.current = null;
    setLoadMode('oss-process');
    loadModeRef.current = 'oss-process';
    setFailed(false);
    setSrc(null);
    setResolving(Boolean(rawUrl || resolvedOssId));

    void (async () => {
      const apply = (next: string | null, mode: LoadMode = 'oss-process') => {
        if (cancelled || attempt !== attemptRef.current) return;
        if (mode === 'oss-process' && next) {
          cropSrcRef.current = next;
        }
        setLoadMode(mode);
        loadModeRef.current = mode;
        setSrc(next);
        setFailed(!next);
        setResolving(false);
      };

      // 嵌入裁剪：签名时带 process，OSS 只返回局部图（禁止先拉整图再 CSS 裁）
      if (record && ossCropProcess && resolvedOssId) {
        try {
          const signedCrop = toHttps(await assetService.getSignedOssUrl(resolvedOssId, {
            process: ossCropProcess,
          }));
          if (cancelled || attempt !== attemptRef.current) return;
          if (signedCrop) {
            apply(signedCrop, 'oss-process');
            return;
          }
        } catch {
          // fall through
        }
      }

      // 签名失败时：公开链拼 x-oss-process（仍走服务端裁切，不用 client-full）
      if (record && ossCropProcess && rawUrl) {
        const next = buildEmbeddedImageSrc({ ...record, sourceImageUrl: rawUrl }, targetPixelWidth);
        if (next) {
          apply(next, 'oss-process');
          return;
        }
      }

      // 无裁剪记录：普通预览签名
      if (resolvedOssId) {
        try {
          const signedUrl = toHttps(await assetService.getSignedOssUrl(resolvedOssId));
          if (cancelled || attempt !== attemptRef.current) return;
          if (signedUrl) {
            apply(signedUrl, 'client-full');
            return;
          }
        } catch {
          // fall through
        }
      }

      if (!rawUrl) {
        apply(null);
        return;
      }
      apply(buildAssetPreviewUrl(rawUrl) || rawUrl, 'client-full');
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedOssId, ossCropProcess, rawUrl, record, targetPixelWidth]);

  const handleError = () => {
    // 签名裁剪失败时，尝试对 rawUrl 拼 process（公开链/未签名场景）
    if (record && ossCropProcess && loadModeRef.current === 'oss-process') {
      const fromRaw = rawUrl
        ? (buildAssetPreviewUrl(withOssProcess(rawUrl, ossCropProcess)) || withOssProcess(rawUrl, ossCropProcess))
        : null;
      if (fromRaw && fromRaw !== src) {
        cropSrcRef.current = fromRaw;
        setSrc(fromRaw);
        setFailed(false);
        setResolving(false);
        return;
      }

      if (EMBEDDED_CROP_CLIENT_FALLBACK_ENABLED) {
        loadModeRef.current = 'client-full';
        setLoadMode('client-full');
        void (async () => {
          try {
            if (resolvedOssId) {
              const signed = toHttps(await assetService.getSignedOssUrl(resolvedOssId));
              if (signed && signed !== src) {
                setSrc(signed);
                setFailed(false);
                setResolving(false);
                return;
              }
            }
          } catch {
            // fall through
          }
          const base = buildAssetPreviewUrl(rawUrl) || rawUrl;
          if (base && base !== src) {
            setSrc(base);
            setFailed(false);
            setResolving(false);
            return;
          }
          setSrc(null);
          setFailed(true);
          setResolving(false);
        })();
        return;
      }
    }

    setSrc(null);
    setFailed(true);
    setResolving(false);
  };

  const wrapZoomable = (content: ReactNode) => {
    if (!zoomable || !src || failed) return content;
    return (
      <>
        <button
          type="button"
          className="block w-full cursor-zoom-in text-left"
          onClick={() => setViewerOpen(true)}
          title="点击放大查看细节"
        >
          {content}
        </button>
        <TtqibZoomableImageOverlay
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          title={resolvedZoomTitle}
          record={record}
          // 放大也用已裁切签名链，避免再拉整图
          fullSourceUrl={cropSrcRef.current || src || undefined}
        />
      </>
    );
  };

  if (resolving && !src) {
    return (
      <div
        className="w-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs rounded border border-gray-200"
        style={{ height }}
      >
        加载中…
      </div>
    );
  }

  if (!src || failed) {
    return (
      <div
        className="w-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs rounded border border-gray-200"
        style={{ height }}
      >
        {rawUrl || resolvedOssId ? '图片加载失败' : '暂无原始素材图'}
      </div>
    );
  }

  if (record) {
    const { cropRect, imageWidth, imageHeight } = record;
    const scale = Math.min(width / cropRect.width, height / cropRect.height);
    const displayWidth = Math.round(cropRect.width * scale);
    const displayHeight = Math.round(cropRect.height * scale);
    const useClientClip = loadMode === 'client-full';

    return wrapZoomable(
      <div
        className="relative overflow-hidden rounded border border-gray-200 bg-white mx-auto"
        style={{ width: displayWidth, height: displayHeight }}
      >
        <img
          src={src}
          alt="原始素材裁剪"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={handleError}
          style={useClientClip ? {
            position: 'absolute',
            width: Math.round(imageWidth * scale),
            height: Math.round(imageHeight * scale),
            left: -Math.round(cropRect.x * scale),
            top: -Math.round(cropRect.y * scale),
            maxWidth: 'none',
          } : {
            // OSS 已裁好：contain 避免再被 cover 裁掉边缘
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </div>,
    );
  }

  return wrapZoomable(
    <div
      className="rounded border border-gray-200 bg-white mx-auto flex items-center justify-center overflow-hidden"
      style={{ width, height }}
    >
      <img
        src={src}
        alt="原始素材"
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={handleError}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
      />
    </div>,
  );
}
